import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import type { ChatDialogData } from '@/entities/ChatDialog/model/types';
import type { ChatDialogResponse } from '@/core/generated/api';
import { operatorChatPinService } from '@/entities/ChatDialog/api/operatorChatPinService';
import { getChatDialogListQuery } from '@/entities/ChatDialog/model/filters';
import { useAdminAccess } from '@/shared/model/adminAccess';
import { getErrorMessage } from '@/shared/lib/errors';

/** Загружает общие закрепления операторов с текущими условиями поиска */
export const useOperatorChatPins = (
    native: boolean,
    external: boolean,
    filterState: Pick<ChatDialogData, 'filter' | 'searchFilter' | 'counters'>,
    pinsRevision = 0,
) => {
    const owner = useAdminAccess((state) => state.snapshot?.appUserId);
    const key = JSON.stringify([owner, native, external, filterState.filter, filterState.searchFilter]);
    const [loadedKey, setLoadedKey] = useState<string | null>(null);
    const [pins, setPins] = useState<ChatDialogResponse[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [pending, setPending] = useState(false);
    const [ready, setReady] = useState(false);
    const epoch = useRef(0);
    const busy = useRef(false);
    const mutation = useRef(0);
    const { filter, searchFilter } = filterState;
    const unread = filterState.counters?.unreadMessages;
    const previousUnread = useRef(unread);
    const previousPinsRevision = useRef(pinsRevision);

    /** Получает закрепления только из разрешённых источников */
    const load = useCallback(async (): Promise<ChatDialogResponse[]> => {
        const query = getChatDialogListQuery({
            filter,
            searchFilter
        });

        const pages = await Promise.all([
            native
                ? operatorChatPinService.getPinned(true, query)
                : Promise.resolve([]),
            external
                ? operatorChatPinService.getPinned(false, query)
                : Promise.resolve([]),
        ]);

        return pages.flat();
    }, [native, external, filter, searchFilter]);

    /** Обновляет общие закрепления с проверкой действующей сессии */
    const reload = useCallback(async (): Promise<void> => {
        const current = ++epoch.current;
        setError(null);
        setReady(false);

        if (!owner) {
            setPins([]);

            return;
        }

        try {
            const result = await load();

            if (current !== epoch.current) return;
            setPins(result);
            setLoadedKey(key);
            setReady(true);
        } catch (cause: unknown) {
            if (current === epoch.current) {
                setError(getErrorMessage(cause, 'Не удалось загрузить закрепления'));
            }
        }
    }, [load, owner, key]);

    useEffect(() => {
        setPins([]);
        busy.current = false;
        mutation.current += 1;
        setPending(false);
        void reload();

        return () => { epoch.current += 1; };
    }, [reload]);

    useEffect(() => {
        const changed = previousUnread.current !== unread;
        previousUnread.current = unread;

        if (changed && filter === 'unread') void reload();
    }, [unread, filter, reload]);

    useEffect(() => {
        const changed = previousPinsRevision.current !== pinsRevision;
        previousPinsRevision.current = pinsRevision;

        if (changed) void reload();
    }, [pinsRevision, reload]);

    /** Сохраняет выбранное закрепление и согласует список с сервером */
    const toggle = async (dialog: ChatDialogResponse): Promise<void> => {
        if (!owner || !ready || busy.current) return;
        busy.current = true;
        setPending(true);
        setError(null);
        const current = ++mutation.current;

        try {
            await operatorChatPinService.setPin(dialog, !dialog.operatorPinnedAt);

            if (current !== mutation.current) return;
            await reload();
        } catch (cause: unknown) {
            if (current === mutation.current) {
                setError(getErrorMessage(cause, 'Не удалось изменить закрепление'));
            }
        } finally {
            if (current === mutation.current) {
                busy.current = false;
                setPending(false);
            }
        }
    };

    return {
        pins: loadedKey === key
            ? pins
            : [],
        error,
        pending,
        ready: ready && loadedKey === key,
        toggle,
        reload,
    };
};
